'use client';

import Link from 'next/link';
import { Box, Typography } from 'src/components';
import { faCalendarUsers, faUsers } from '@fortawesome/pro-solid-svg-icons';
import { VolumeTypeBox } from '../VolumeTypeBox';
import { useEncounters } from 'src/context/providers/EncountersProvider';
import { VolumeType, EncountersActionTypes } from 'src/context/reducers/encountersReducer';
import { useFlags } from 'launchdarkly-react-client-sdk';

interface VolumeTypeSelectionProps {
  hideEncounterType?: boolean;
}

const VolumeTypeSelection: React.FC<VolumeTypeSelectionProps> = ({
  hideEncounterType = false,
}): React.ReactElement => {
  const { state, dispatch } = useEncounters();
  const { volumeType: selectedType, billingCodes } = state;
  const { newEncounters } = useFlags();

  const handleTypeChange = (type: VolumeType) => {
    const secondGroupId = billingCodes?.groups?.[1]?.id;

    dispatch({
      type: EncountersActionTypes.SET_VOLUME_TYPE,
      payload: { volumeType: type },
    });
    if (type === VolumeType.TOTAL_CLAIMS) {
      dispatch({
        type: EncountersActionTypes.REMOVE_CODE_GROUP,
        payload: { groupId: secondGroupId },
      });
      dispatch({
        type: EncountersActionTypes.SET_SHOW_ADD_GROUP,
        payload: { showAddGroup: false },
      });
    }
  };

  const volumeTypeData = {
    [VolumeType.TOTAL_CLAIMS]: {
      icon: faUsers,
      title: 'Total Claims',
      description:
        'Target patients who match the criteria present across any claims.',
      showNewBadge: false,
    },
    [VolumeType.ENCOUNTERS]: {
      icon: faCalendarUsers,
      title: 'Encounters',
      description:
        'Target patients who match the criteria present during the same Encounter.',
      showNewBadge: false,
    },
  };

  return (
    <Box className="flex flex-col gap-6 p-6 border-b border-neutral-300">
      <Box className="flex flex-col">
        <Typography className="!text-primary text-lg font-bold leading-7">
          Volume Type
        </Typography>
        <Typography className="self-stretch justify-start !text-tertiary text-sm font-normal leading-tight">
          Choose the type of claims you want to target.
        </Typography>
      </Box>
      <Box className="flex flex-row w-full justify-center gap-8">
        <VolumeTypeBox
          type={VolumeType.TOTAL_CLAIMS}
          selected={selectedType === VolumeType.TOTAL_CLAIMS}
          title={volumeTypeData[VolumeType.TOTAL_CLAIMS].title}
          description={volumeTypeData[VolumeType.TOTAL_CLAIMS].description}
          showNewBadge={volumeTypeData[VolumeType.TOTAL_CLAIMS].showNewBadge}
          onChange={handleTypeChange}
        />
        {!hideEncounterType && (
          <VolumeTypeBox
            type={VolumeType.ENCOUNTERS}
            selected={selectedType === VolumeType.ENCOUNTERS}
            title={volumeTypeData[VolumeType.ENCOUNTERS].title}
            description={volumeTypeData[VolumeType.ENCOUNTERS].description}
            showNewBadge={
              volumeTypeData[VolumeType.ENCOUNTERS].showNewBadge && newEncounters
            }
            onChange={handleTypeChange}
            disabled={!newEncounters}
          />
        )}
      </Box>
      {!hideEncounterType && (
        <Typography className="!text-tertiary text-xs w-full text-right font-normal leading-none flex justify-end gap-1">
          <Link
            href="https://help.medscout.io/en/articles/13121081-what-are-encounters"
            target="_blank"
            className="underline"
          >
            Learn more
          </Link>
          <span>about Encounters.</span>
        </Typography>
      )}
    </Box>
  );
};

export default VolumeTypeSelection;
